import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Home as HomeIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { getProjects } from '../services/api';

const Home = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (error) {
        console.error('Error loading projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const residentialProjects = projects.filter(p => p.category === 'residential');
  const commercialProjects = projects.filter(p => p.category === 'commercial');

  // Category images - hardcoded from Google Photos
  const categoryImages = {
    residential: 'https://lh3.googleusercontent.com/pw/AP1GczPLkS7QiaJHxazMXW_Y8JOmF_2D01oRLGWjfJGLBy2DserbnI_ZYja6D9QjrcC1wlu8NfDw6eTbXKbtGP0u-fNbwDEmY2py_3SiFtGUvlSgtdEkdScC=w2000',
    commercial: 'https://lh3.googleusercontent.com/pw/AP1GczP4q6_J_pdhAgmW1QSiUqX1DiukJZS-FwdvDdmkpkEY00FP8AQExUu5BiTIB22vf3qYOxTtATXfg6dI9lNTbOFLouPn-4nzsAS9utjN_iGQxO8Hk2zs=w2000'
  };

  // Hero slideshow images
  const heroImages = [
    'https://lh3.googleusercontent.com/pw/AP1GczOZaJS-me1rKCLD9QMvMSCGJXzvrGC_A9r-bnHadafmcIlc5MpaO69E59CPgRs5s2Fh3II67_SMato775OCLsf2fbjJosHnhN_IDmUB2jJR9cbTWHhf=w2000',
    'https://lh3.googleusercontent.com/pw/AP1GczO3U9cs3MDnMY9kTM5oj7p7--eTtgPl35QSu8eAqrzPOBhVADBG-b2PcPA7nsQVq8mh0UoL7v-igHYXOEfiUMKUyiuVUwAbYCM2KKqyCRJ82-juoSHP=w2000',
    'https://lh3.googleusercontent.com/pw/AP1GczMjX1u1EXD0p_zPQ07nFrgMW9pPkSvIEJUhWxKirEly6X92xB3_S5_NnuV3ykm8Zs7zJIGEhqklskyjuKVhRT6fnQGwbEt0RxyFbCE-eE8OeVKDbaXw=w2000',
    'https://lh3.googleusercontent.com/pw/AP1GczP3c3DdRSXsEuku6wKIdDW5gmUJ7L5RBr-aTLN-c4JsWIoAFCuqP_2yd7IO9b5hGXnqjLUBHAOkdd6XofD6-HDWqBjINrLYrseugf6uPrwRu1EwtcT6=w2000',
    'https://lh3.googleusercontent.com/pw/AP1GczOYOh9ulPgp4ToSCWxHS05W04qgWR4O69SM8rDC3BhMmgo4bQrHXX0d4liBiSUliiO8KU3P3nMo8CPw8IJIBKDsSBA_EilA2en8j5uvJikCST-PzvTr=w2000',
    'https://lh3.googleusercontent.com/pw/AP1GczOce0szbUuFXNm6GFEqV2THWlLIygRF7qm6aAR9QAr7UDPWzg6btotEmpWcEm2K2MXkN_NNSjKlDP7OtPf5-Xk_vs6G9HuJM9Og2pshKUyfsikdgWuB=w2000'
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  // Auto-advance slideshow
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [heroImages.length]);

  return (
    <div className="min-h-screen bg-[#0f0f10]">
      {/* Hero Section with Slideshow - Horizontal Format */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        {/* Background Slideshow */}
        <div className="absolute inset-0">
          {heroImages.map((image, index) => (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={image}
                alt={`Architectural photography ${index + 1}`}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f0f10]/70 via-[#0f0f10]/50 to-[#0f0f10]"></div>
        </div>

        {/* Slideshow Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 md:left-8 z-20 p-3 bg-[#1a1a1a]/80 hover:bg-[#4b63eb] text-[#f5f5f5] transition-all duration-300 backdrop-blur-sm"
          aria-label="Previous slide"
        >
          <ChevronLeft size={32} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 md:right-8 z-20 p-3 bg-[#1a1a1a]/80 hover:bg-[#4b63eb] text-[#f5f5f5] transition-all duration-300 backdrop-blur-sm"
          aria-label="Next slide"
        >
          <ChevronRight size={32} />
        </button>

        {/* Slide Indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex space-x-3">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-[#4b63eb] scale-125' : 'bg-[#f5f5f5]/50'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
          <h1 className="text-5xl md:text-7xl font-bold tracking-wider mb-4 animate-fade-in">
            <span className="text-[#4b63eb]">JACKSONVILLE</span>
            <span className="text-[#f5f5f5] ml-4">PHOTO</span>
          </h1>
          <p className="text-lg md:text-2xl text-[#f5f5f5] font-light tracking-widest uppercase mb-3">
            Architectural Photography
          </p>
          <p className="text-base text-[#a0a0a0] italic mb-8">
            Capturing spaces that inspire
          </p>
          <Link
            to="/portfolio"
            className="inline-flex items-center space-x-2 bg-gradient-to-r from-[#4b63eb] to-[#3b4ec9] text-[#f5f5f5] px-8 py-3 text-sm tracking-widest uppercase font-semibold transition-all duration-300 hover:shadow-lg hover:shadow-[#4b63eb]/50 hover:scale-105"
          >
            <span>View Portfolio</span>
            <ArrowRight size={20} />
          </Link>
        </div>

        {/* Scroll Indicator - Removed since hero is shorter */}
      </section>

      {/* Categories Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-[#f5f5f5] mb-4 tracking-wider">
            Our <span className="text-[#4b63eb]">Expertise</span>
          </h2>
          <p className="text-[#a0a0a0] text-lg">
            Specializing in residential and commercial architectural photography
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Residential */}
          {!loading && (
          <Link
            to="/portfolio?category=residential"
            className="group relative h-[500px] overflow-hidden bg-[#1a1a1a] transition-all duration-500 hover:shadow-2xl hover:shadow-[#4b63eb]/20"
          >
            <img
              src={categoryImages.residential}
              alt="Residential interiors"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] via-[#0f0f10]/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
              <HomeIcon size={48} className="text-[#4b63eb] mb-4 transition-transform duration-300 group-hover:scale-110" />
              <h3 className="text-3xl font-bold text-[#f5f5f5] mb-3 tracking-wider">RESIDENTIAL</h3>
              <p className="text-[#a0a0a0] mb-4">Interiors that tell a story</p>
              <div className="flex items-center space-x-2 text-[#4b63eb] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm tracking-widest uppercase">Explore</span>
                <ArrowRight size={18} />
              </div>
            </div>
          </Link>
          )}

          {/* Commercial */}
          {!loading && (
          <Link
            to="/portfolio?category=commercial"
            className="group relative h-[500px] overflow-hidden bg-[#1a1a1a] transition-all duration-500 hover:shadow-2xl hover:shadow-[#4b63eb]/20"
          >
            <img
              src={categoryImages.commercial}
              alt="Commercial exteriors"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f10] via-[#0f0f10]/50 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-300"></div>
            <div className="absolute inset-0 flex flex-col items-center justify-center p-8 text-center">
              <Building2 size={48} className="text-[#4b63eb] mb-4 transition-transform duration-300 group-hover:scale-110" />
              <h3 className="text-3xl font-bold text-[#f5f5f5] mb-3 tracking-wider">COMMERCIAL</h3>
              <p className="text-[#a0a0a0] mb-4">Exteriors that inspire</p>
              <div className="flex items-center space-x-2 text-[#4b63eb] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-sm tracking-widest uppercase">Explore</span>
                <ArrowRight size={18} />
              </div>
            </div>
          </Link>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#1a1a1a] to-[#0f0f10]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-[#f5f5f5] mb-6 tracking-wider">
            Ready to Showcase Your <span className="text-[#4b63eb]">Architecture</span>?
          </h2>
          <p className="text-lg text-[#a0a0a0] mb-10 leading-relaxed">
            Let's create stunning visual stories that highlight the unique character of your spaces.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center space-x-2 bg-[#f5f5f5] text-[#1a1a1a] px-10 py-4 text-sm tracking-widest uppercase font-semibold transition-all duration-300 hover:bg-[#4b63eb] hover:text-[#f5f5f5] hover:shadow-lg hover:shadow-[#4b63eb]/30"
          >
            <span>Get In Touch</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
